import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Dream, DreamConfig, DreamProjection } from '../../types/dreamSimulator';
import { formatCurrency } from '../../utils/formatters';
import { Investment } from '../../types';
import { compoundMultiplierContent } from '../../data/compoundMultiplierContent';
import { 
  ChevronRight, 
  ChevronLeft,
  Play,
  Pause,
  RotateCcw,
  SkipForward,
  SkipBack,
  Zap,
  Target,
  Award,
  Calendar,
  Settings,
  Download,
  FileText,
  CheckCircle,
  Activity,
  TrendingUp,
  DollarSign
} from 'lucide-react';
import { calculateProjections } from '../../utils/calculations/dreamSimulatorCalculations';

interface CompoundInterestAnimationProps {
  dream: Dream;
  config: DreamConfig;
  projections: DreamProjection[];
  onContinue: () => void;
  investment: Investment;
  selectedComparisons: string[];
  onSelectedComparisonsChange: (comparisons: string[]) => void;
}

const CompoundInterestAnimation: React.FC<CompoundInterestAnimationProps> = ({ 
  dream, 
  config, 
  projections,
  onContinue,
  investment,
  selectedComparisons,
  onSelectedComparisonsChange
}) => {
  const [currentYear, setCurrentYear] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationSpeed, setAnimationSpeed] = useState(1);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);
  const [animationPhase, setAnimationPhase] = useState<'intro' | 'animation' | 'complete'>('intro');

  const Icon = dream.icon;
  
  // Get current projection data
  const currentProjection = projections[currentYear] || projections[0];
  const finalProjection = projections[projections.length - 1];
  
  // Calculate goal achievement
  const currentIncome = currentProjection?.monthlyIncome || 0;
  const progressPercentage = config.monthlyGoal ? (currentIncome / config.monthlyGoal) * 100 : 0;
  const isGoalAchieved = progressPercentage >= 100;
  
  // Animation control functions
  const startAnimation = () => {
    if (animationPhase === 'intro') {
      setAnimationPhase('animation');
      setCurrentYear(0);
    }
    setIsAnimating(true);
    
    const interval = setInterval(() => {
      setCurrentYear(prev => {
        const nextYear = prev + 1;
        if (nextYear >= config.timeframe) {
          setIsAnimating(false);
          return config.timeframe - 1;
        }
        return nextYear;
      });
    }, 1000 / animationSpeed);
    
    setIntervalId(interval);
  };

  const pauseAnimation = () => {
    setIsAnimating(false);
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
  };

  const resetAnimation = () => {
    pauseAnimation();
    setAnimationPhase('intro');
    setCurrentYear(0);
  };

  const skipToEnd = () => {
    pauseAnimation();
    setCurrentYear(config.timeframe - 1);
  };

  const changeSpeed = (speed: number) => {
    setAnimationSpeed(speed);
    if (isAnimating) {
      pauseAnimation();
      setAnimationSpeed(speed);
      startAnimation();
    }
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [intervalId]);

  // Calculate dynamic financial metrics for current year
  const calculateCurrentMetrics = () => {
    if (!currentProjection) {
      return {
        monthlyIncome: 0,
        patrimony: config.certificatesNeeded * investment.certificateBasePrice,
        capitalGain: 0
      };
    }

    // Calculate certificate price for current year
    let certificatePrice = investment.certificateBasePrice;
    if (currentYear <= 5) {
      certificatePrice = investment.certificateBasePrice * Math.pow(1 + investment.appreciationRate / 100, currentYear);
    } else {
      const maxAppreciatedValue = investment.certificateBasePrice * Math.pow(1 + investment.appreciationRate / 100, 5);
      if (investment.increaseLemonPrice) {
        certificatePrice = maxAppreciatedValue * Math.pow(1 + investment.lemonPriceIncrease / 100, currentYear - 5);
      } else {
        certificatePrice = maxAppreciatedValue;
      }
    }

    // Calculate monthly income (only after year 4)
    let monthlyIncome = 0;
    if (currentYear > 4) {
      const producingCertificates = currentProjection.producingCertificates || 0;
      let currentLemonPrice = investment.averageSalePricePerKg;
      if (currentYear > 5 && investment.increaseLemonPrice) {
        currentLemonPrice = investment.averageSalePricePerKg * Math.pow(1 + investment.lemonPriceIncrease / 100, currentYear - 5);
      }
      monthlyIncome = (producingCertificates * investment.averageProductionPerHectare * currentLemonPrice * 0.1 * 0.65) / 12;
    }

    // Calculate total patrimony
    const totalCertificates = currentProjection.totalCertificates || config.certificatesNeeded;
    const patrimony = totalCertificates * certificatePrice;

    // Calculate capital gain (plusvalía)
    const initialInvestment = config.certificatesNeeded * investment.certificateBasePrice;
    const capitalGain = patrimony - initialInvestment;

    return {
      monthlyIncome,
      patrimony,
      capitalGain
    };
  };

  const currentMetrics = calculateCurrentMetrics();

  // Render intro phase
  if (animationPhase === 'intro') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-4xl mx-auto"
      >
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-8 text-white">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                <Icon className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Interés Compuesto Multiplicador™ - {dream.name}</h2>
                <p className="text-indigo-100">{compoundMultiplierContent.tagline}</p>
              </div>
            </div>
          </div>
          
          <div className="p-8 space-y-8">
            <div className="text-center">
              <h3 className="text-3xl font-bold text-gray-800 mb-4">{dream.name}</h3>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">{dream.description}</p>
              
              <div className="mb-8 p-4 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl">
                <h4 className="text-lg font-semibold text-blue-800 mb-2">Interés Compuesto Multiplicador™</h4>
                <p className="text-sm text-blue-700">
                  No solo multiplicaremos tu dinero, multiplicaremos tus activos productivos reales
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 max-w-2xl mx-auto">
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl">
                  <div className="text-sm text-green-600 mb-1">Meta Mensual</div>
                  <div className="text-xl font-bold text-green-700">
                    {formatCurrency(config.monthlyGoal, investment.currencyFormat)}
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl">
                  <div className="text-sm text-blue-600 mb-1">Plazo</div>
                  <div className="text-xl font-bold text-blue-700">
                    {config.timeframe} años
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl">
                  <div className="text-sm text-purple-600 mb-1">Certificados</div>
                  <div className="text-xl font-bold text-purple-700">
                    {config.certificatesNeeded}
                  </div>
                </div>
              </div>
              
              <button
                onClick={startAnimation}
                className="px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-3 mx-auto"
              >
                <Play className="w-6 h-6" />
                <span>Iniciar Simulación Multiplicador™</span>
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Render animation phase
  if (animationPhase === 'animation') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-6xl mx-auto"
      >
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-8 text-white">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                <Zap className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">El Efecto Avalancha en Acción</h2>
                <p className="text-indigo-100">Observa cómo se multiplican tus activos productivos año tras año</p>
              </div>
            </div>
          </div>
          
          <div className="p-8 space-y-8">
            {/* Animation Controls */}
            <div className="flex justify-center items-center gap-4 mb-8">
              <div className="flex gap-2">
                {[0.5, 1, 2, 4].map(speed => (
                  <button
                    key={speed}
                    onClick={() => changeSpeed(speed)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      animationSpeed === speed
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {speed}x
                  </button>
                ))}
              </div>
              
              <button
                onClick={isAnimating ? pauseAnimation : startAnimation}
                className="p-3 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors"
              >
                {isAnimating ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
              </button>
              
              <button
                onClick={skipToEnd}
                className="p-3 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors"
              >
                <SkipForward className="w-6 h-6" />
              </button>
              
              <button
                onClick={resetAnimation}
                className="p-3 bg-gray-600 text-white rounded-full hover:bg-gray-700 transition-colors"
              >
                <RotateCcw className="w-6 h-6" />
              </button>
              
              <div className="flex items-center justify-center w-16 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg shadow-md">
                <div className="text-center">
                  <div className="text-lg font-bold leading-none">
                    {currentYear + 1}
                  </div>
                  <div className="text-xs opacity-80">año</div>
                </div>
              </div>
            </div>

            {/* Current Year Display */}
            <div className="text-center mb-8">
              <div className="text-6xl font-bold text-indigo-600 mb-2">
                Año {currentYear + 1}
              </div>
              <div className="text-xl text-gray-600">
                {(() => {
                  if (currentYear < 4) return "Período de Maduración";
                  if (currentYear === 4) return "¡Primera Producción!";
                  return "Efecto Avalancha";
                })()}
              </div>
              <div className="text-lg text-gray-500 mt-2">
                {(() => {
                  if (currentYear < 4) return "Los certificados están creciendo y apreciándose";
                  if (currentYear === 4) return "Comienzan a generar utilidades que se reinvierten";
                  const totalCerts = currentProjection?.totalCertificates || config.certificatesNeeded;
                  const freeCerts = Math.max(0, totalCerts - config.certificatesNeeded);
                  return freeCerts > 0 
                    ? `¡El Multiplicador™ ya te dio ${freeCerts} certificado${freeCerts !== 1 ? 's' : ''} GRATIS! Sigues creciendo...`
                    : "El Multiplicador™ está acumulando utilidades para darte certificados gratis";
                })()}
              </div>
            </div>

            {/* Certificate Visualization */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-2xl p-8 mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-6 text-center">
                Tus Certificados Productivos
              </h3>
              
              <div className="text-center mb-6">
                <div className="text-4xl font-bold text-green-600 mb-2">
                  {currentProjection?.totalCertificates || config.certificatesNeeded} certificados
                </div>
                <div className="text-sm text-gray-600">
                  = {((currentProjection?.totalCertificates || config.certificatesNeeded) * 1000).toLocaleString()} m² de plantación productiva
                </div>
              </div>

              {/* Visual representation of certificates */}
              <div className="flex justify-center mb-6 flex-wrap gap-2">
                {/* Initial certificates */}
                {Array.from({ length: Math.min(config.certificatesNeeded, 10) }).map((_, i) => (
                  <motion.div
                    key={`initial-${i}`}
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold shadow-md bg-gradient-to-br from-green-500 to-green-600"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ 
                      delay: i * 0.1, 
                      duration: 0.5,
                      type: "spring",
                      stiffness: 200 
                    }}
                  >
                    {i + 1}
                  </motion.div>
                ))}
                
                {/* Additional certificates from reinvestment */}
                {currentProjection && currentProjection.totalCertificates > config.certificatesNeeded && 
                  Array.from({ 
                    length: Math.min(
                      currentProjection.totalCertificates - config.certificatesNeeded, 
                      10
                    ) 
                  }).map((_, i) => (
                    <motion.div
                      key={`reinvested-${i}`}
                      className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold shadow-md bg-gradient-to-br from-blue-500 to-blue-600"
                      initial={{ scale: 0, opacity: 0, rotate: -180 }}
                      animate={{ scale: 1, opacity: 1, rotate: 0 }}
                      transition={{ 
                        delay: config.certificatesNeeded * 0.1 + i * 0.2, 
                        duration: 0.6,
                        type: "spring",
                        stiffness: 150
                      }}
                    >
                      {config.certificatesNeeded + i + 1}
                    </motion.div>
                  ))
                }
                
                {/* Show count if too many certificates */}
                {currentProjection && currentProjection.totalCertificates > (config.certificatesNeeded + 10) && (
                  <motion.div
                    className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center text-white font-bold shadow-md"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 2, duration: 0.3 }}
                  >
                    +{currentProjection.totalCertificates - (config.certificatesNeeded + 10)}
                  </motion.div>
                )}
              </div>

              {/* Dynamic Financial Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                  <DollarSign className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <div className="text-lg font-bold text-green-700">
                    {formatCurrency(currentMetrics.monthlyIncome, investment.currencyFormat)}
                  </div>
                  <div className="text-sm text-green-600">ingreso mensual</div>
                </div>
                
                <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                  <TrendingUp className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <div className="text-lg font-bold text-blue-700">
                    {formatCurrency(currentMetrics.patrimony, investment.currencyFormat)}
                  </div>
                  <div className="text-sm text-blue-600">patrimonio total</div>
                </div>
                
                <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                  <Award className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                  <div className="text-lg font-bold text-purple-700">
                    {formatCurrency(currentMetrics.capitalGain, investment.currencyFormat)}
                  </div>
                  <div className="text-sm text-purple-600">plusvalía acumulada</div>
                </div>
              </div>
            </div>

            {/* Progress towards goal */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-800">Multiplicador™ Construyendo tu Sueño</h3>
                <div className="text-2xl font-bold text-indigo-600">
                  {progressPercentage.toFixed(1)}%
                </div>
              </div>
              
              <div className="mb-4 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-700 text-center">
                  <strong>Año {currentProjection?.year || 1}:</strong> Compraste {config.certificatesNeeded} certificado{config.certificatesNeeded > 1 ? 's' : ''}, 
                  ahora tienes {currentProjection?.totalCertificates || config.certificatesNeeded} certificados productivos. 
                  <strong>¡{Math.max(0, (currentProjection?.totalCertificates || config.certificatesNeeded) - config.certificatesNeeded)} certificados GRATIS!</strong>
                </p>
              </div>
              
              <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
                <motion.div
                  className="bg-gradient-to-r from-indigo-500 to-purple-600 h-4 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(100, progressPercentage)}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-sm text-gray-600 mb-1">Meta Mensual</div>
                  <div className="text-lg font-bold text-gray-800">
                    {formatCurrency(config.monthlyGoal, investment.currencyFormat)}
                  </div>
                </div>
                
                <div className="text-center">
                  <div className="text-sm text-gray-600 mb-1">Ingreso Actual</div>
                  <div className="text-lg font-bold text-green-600">
                    {formatCurrency(currentProjection?.monthlyIncome || 0, investment.currencyFormat)}
                  </div>
                </div>
                
                <div className="text-center">
                  <div className="text-sm text-gray-600 mb-1">Certificados</div>
                  <div className="text-lg font-bold text-purple-600">
                    {currentProjection?.totalCertificates || config.certificatesNeeded}
                  </div>
                  <div className="text-xs text-purple-500 mt-1">activos productivos</div>
                </div>
                
                <div className="text-center">
                  <div className="text-sm text-gray-600 mb-1">Patrimonio</div>
                  <div className="text-lg font-bold text-blue-600">
                    {formatCurrency(currentProjection?.patrimony || 0, investment.currencyFormat)}
                  </div>
                  <div className="text-xs text-blue-500 mt-1">valor de activos reales</div>
                </div>
              </div>
            </div>

            {/* Year progress bar */}
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Progreso Temporal</span>
                <span className="text-sm text-gray-500">
                  {currentYear + 1} / {config.timeframe} años
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${((currentYear + 1) / config.timeframe) * 100}%` }}
                />
              </div>
            </div>

            {/* Continue Button - Solo mostrar cuando la animación termine */}
            {!isAnimating && currentYear >= config.timeframe - 1 && (
              <div className="flex justify-center mt-8">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onContinue}
                  className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-3"
                >
                  <ChevronRight className="w-6 h-6" />
                  <span>Continuar a Gráficos</span>
                </motion.button>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  // This shouldn't be reached in normal flow, but just in case
  return null;
};

export default CompoundInterestAnimation;